/**
 * scheduleBus.ts
 * Tiny in-app pub/sub so a schedule mutation in ONE view (Machine Schedule, Scenario
 * Planning, the Employee Schedule itself) can quietly refresh the OTHER views without
 * a full reload or waiting on the 45s poller. Process-local only — no backend, no
 * websocket, no migration. Subscribers do a QUIET background revalidate (no remount,
 * no full-screen gate, no progress-bar flash).
 *
 * Emit after a mutation SUCCEEDS (booking date/machine/qty change, assignment add /
 * remove / percent / reassign). Subscribers debounce so a flurry of edits coalesces.
 */

export type ScheduleChangeKind = 'bookings-changed' | 'assignments-changed' | 'skills-changed';

export interface ScheduleChange {
  kind: ScheduleChangeKind;
  /** Optional hint: the task whose bookings/assignments changed. */
  taskKey?: string | null;
  /** Optional hint: affected users (for future targeted patching). */
  userKeys?: string[];
  /**
   * Identifies the emitter so a view can ignore its OWN echo if it already patched
   * optimistically (optional — most subscribers just refetch quietly).
   */
  source?: string;
}

type Listener = (change: ScheduleChange) => void;

const listeners = new Set<Listener>();

/** Notify every subscriber that schedule data changed. Never throws. */
export function emitScheduleChange(change: ScheduleChange): void {
  // Copy so a listener that unsubscribes during dispatch can't mutate the live set.
  for (const listener of [...listeners]) {
    try {
      listener(change);
    } catch {
      // A misbehaving subscriber must never break the emitter or sibling listeners.
    }
  }
}

/** Subscribe to schedule changes. Returns an unsubscribe fn (call in a cleanup). */
export function onScheduleChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
